import { ColDef, ValueGetterParams } from 'ag-grid-community'
import { AuditDetail, Collection, DealDetail, OrderDetail } from 'api'
import { Locale, Translation } from 'locale'
import { CollectionColumnId, ISSUE, MARKET_TYPE, MIC, ROE, ROS } from 'view/routes/ColumnNames'
import {
  assetTypeFormatter,
  booleanFormatter,
  collectionTypeFormatter,
  defaultFormatter,
  directionFormatter,
  directionQuickFilterTextGetter,
  issueFormatter,
  marketTypeFormatter,
  repoTypeFormatter
} from '../modelFormatters'
import { makeBooleanQuickFilterTextGetter } from '../baseFormatters'
import { TypedValueGetterParams, ValidationCellRenderer } from 'view/components/datagrid'
import { DirectionCellRenderer } from '../DirectionCellRenderer'
import { DirectionSelectbox } from 'view/shared/entity'

type BaseColDef<T = unknown> = Required<Pick<ColDef<T>, 'headerName' | 'valueFormatter' | 'width' | 'filter'>> & ColDef<T>

const BASE_READ_FIELD = {
  editable: false,
  filter: 'agSetColumnFilter',
  width: 90,
  valueFormatter: defaultFormatter
}

export const BASE_COLLECTION_TYPE: BaseColDef = {
  colId: CollectionColumnId.COLLECTION_TYPE,
  filter: 'agSetColumnFilter',
  headerName: Locale.translate(Translation.TC_COLLECTION_TYPE),
  keyCreator: collectionTypeFormatter,
  valueFormatter: collectionTypeFormatter,
  valueGetter: (params: ValueGetterParams<{ collection?: Collection }>) => params?.data?.collection?.type,
  filterParams: { valueFormatter: collectionTypeFormatter },
  width: 150
}

export const BASE_REPO_TYPE: BaseColDef = {
  filter: 'agSetColumnFilter',
  headerName: Locale.translate(Translation.TC_REPO_TYPE),
  keyCreator: repoTypeFormatter,
  valueFormatter: repoTypeFormatter,
  filterParams: { valueFormatter: repoTypeFormatter },
  width: 100
}

const BASE_BOOLEAN_COL = {
  filter: 'agSetColumnFilter',
  width: 120,
  valueFormatter: booleanFormatter,
  getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false }),
  cellRenderer: ValidationCellRenderer
}

export const BASE_ROE: BaseColDef = {
  ...BASE_BOOLEAN_COL,
  colId: ROE,
  headerName: Locale.translate(Translation.TC_ROE)
}

export const BASE_ROS: BaseColDef = {
  ...BASE_BOOLEAN_COL,
  colId: ROS,
  headerName: Locale.translate(Translation.TC_ROS)
}

export const BASE_MARKET_TYPE: BaseColDef = {
  ...BASE_READ_FIELD,
  colId: MARKET_TYPE,
  headerName: Locale.translate(Translation.TC_MARKET_TYPE),
  valueFormatter: marketTypeFormatter,
  hide: true,
  valueGetter: (params: TypedValueGetterParams<AuditDetail | DealDetail | OrderDetail>) => {
    const data = params.data
    if (!data) return null

    if ('negotiation' in data) return data.negotiation?.marketType ?? null
    if ('deal' in data) return data.deal?.marketType ?? null
    if ('order' in data) return data.order?.marketType ?? null

    return null
  }
}

export const BASE_MIC: BaseColDef = {
  ...BASE_READ_FIELD,
  colId: MIC,
  headerName: Locale.translate(Translation.TC_MIC)
}

export const BASE_ISSUE: BaseColDef = {
  colId: ISSUE,
  valueFormatter: issueFormatter,
  headerName: Locale.translate(Translation.TC_ISSUE),
  filter: 'agSetColumnFilter',
  width: 150
}

export const BASE_ASSET_TYPE = {
  valueFormatter: assetTypeFormatter,
  headerName: Locale.translate(Translation.TC_ASSET_TYPE),
  filter: 'agSetColumnFilter'
}

export const BASE_DIRECTION: BaseColDef = {
  filter: 'agSetColumnFilter',
  valueFormatter: directionFormatter,
  headerName: Locale.translate(Translation.TC_DIRECTION),
  getQuickFilterText: directionQuickFilterTextGetter,
  cellRenderer: DirectionCellRenderer,
  width: 90,
  cellEditorParams: {
    inputComponent: DirectionSelectbox
  }
}
